import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../services/api";
import { toast } from "react-toastify";
import { CartContext } from "../context/CartContext";
import ReactDOM from "react-dom";
import StarRating from "./StarRating";
import ImageLightbox from "./ImageLightbox";
import giftDeodorant from "../assets/quà tặng/chai khử mùi.jpg";
import giftSocks from "../assets/quà tặng/vớ.png";

const ProductQuickView = ({
  product,
  productId: propProductId,
  onClose,
  onAddToCart,
}) => {
  const { addToCart } = useContext(CartContext);
  const navigate = useNavigate();

  const [productDetail, setProductDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  const user = JSON.parse(localStorage.getItem("user"));
  const productId = propProductId || product._id?.$oid || product._id;

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get(`/products/${productId}`);
        setProductDetail(res.data);
      } catch (err) {
        console.error("Error fetching product:", err);
        setError(
          err.response?.data?.error || "Không thể tải thông tin sản phẩm.",
        );
        setProductDetail(product);
      } finally {
        setLoading(false);
      }
    };

    if (productId) fetchProduct();
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [productId]);

  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!user) return;
      try {
        const res = await api.get("/favorites");
        const favoritesList = res.data.products || [];
        const isFav = favoritesList.some(
          (favItem) => favItem._id === productId,
        );
        setIsFavorite(isFav);
      } catch (err) {
        console.error("Error fetching favorites:", err);
      }
    };
    if (productId && user) checkFavoriteStatus();
  }, [productId, user]);

  const formatPrice = (price) => new Intl.NumberFormat("vi-VN").format(price);

  const handleAddToCart = () => {
    if (!productDetail) return;
    if (productDetail.colors?.length > 0 && !selectedColor) {
      toast.warn("Vui lòng chọn màu sắc sản phẩm!");
      return;
    }
    if (productDetail.sizes?.length > 0 && !selectedSize) {
      toast.warn("Vui lòng chọn kích thước sản phẩm!");
      return;
    }

    const payload = {
      productId: productDetail._id,
      qty: quantity,
      selectedColor: selectedColor || "Mặc định",
      selectedSize: selectedSize || "One Size",
      price: productDetail.price,
      name: productDetail.name,
      image: productDetail.image || productDetail.images?.[0],
    };

    addToCart(payload);
    toast.success("✅ Đã thêm vào giỏ hàng!");
    onClose();
    if (onAddToCart) onAddToCart();
  };

  const handleBuyNow = async () => {
    if (!productDetail) return;
    if (productDetail.colors?.length > 0 && !selectedColor) {
      toast.warn("Vui lòng chọn màu sắc sản phẩm!");
      return;
    }
    if (productDetail.sizes?.length > 0 && !selectedSize) {
      toast.warn("Vui lòng chọn kích thước sản phẩm!");
      return;
    }

    const payload = {
      productId: productDetail._id,
      qty: quantity,
      selectedColor: selectedColor || "Mặc định",
      selectedSize: selectedSize || "One Size",
      price: productDetail.price,
      name: productDetail.name,
      image: productDetail.image || productDetail.images?.[0],
    };
    await addToCart(payload);
    onClose();
    navigate("/checkout");
  };

  const handleToggleFavorite = async () => {
    if (!user) {
      toast.info("Vui lòng đăng nhập để lưu sản phẩm yêu thích");
      return;
    }
    try {
      const res = await api.post("/favorites/toggle", { productId });
      setIsFavorite(res.data.isFavorite);
      toast.success(res.data.message);
    } catch (err) {
      console.error("Error toggling favorite:", err);
      toast.error(err.response?.data?.error || "Có lỗi xảy ra");
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center">
        <div className="bg-white dark:bg-slate-900 rounded-lg p-8 text-center border border-transparent dark:border-slate-800 transition-colors">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-slate-400">
            Đang tải thông tin sản phẩm...
          </p>
        </div>
      </div>
    );
  }

  const images =
    productDetail?.images?.length > 0
      ? productDetail.images
      : [productDetail?.image || product?.image];
  const defaultImage = "https://placehold.co/400x400?text=No+Image";

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center sm:p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-2xl w-full sm:max-w-5xl max-h-[90vh] sm:max-h-[90vh] overflow-hidden relative animate-slideUp sm:animate-zoomIn shadow-2xl flex flex-col mt-auto sm:mt-0 border-t border-gray-100 dark:border-slate-800 sm:border-none"
        onClick={(e) => e.stopPropagation()}
        style={{
          zoom: "0.8",
          MozTransform: "scale(0.8)",
          MozTransformOrigin: "center center",
        }}
      >
        {/* Mobile Header - 2 Column Layout */}
        <div className="sm:hidden grid grid-cols-[1fr_1fr] gap-3 p-3 border-b border-gray-100 dark:border-slate-800 sticky top-0 bg-white dark:bg-slate-900 z-10 relative">
          {/* Left - Large Product Image */}
          <div
            className="aspect-square bg-gray-50 dark:bg-slate-800 rounded-xl overflow-hidden border border-gray-200 dark:border-slate-700 cursor-zoom-in active:scale-[0.98] transition-transform"
            onClick={() => setIsLightboxOpen(true)}
          >
            <img
              src={images[selectedImage] || defaultImage}
              alt={productDetail?.name}
              className="w-full h-full object-contain p-3"
              onError={(e) => {
                e.target.src = defaultImage;
              }}
            />
          </div>

          {/* Right - Product Info */}
          <div className="flex flex-col justify-between py-0.5">
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <span className="text-lg font-bold text-red-600">
                  {formatPrice(productDetail?.price)}₫
                </span>
                {productDetail?.discount > 0 && (
                  <span className="bg-red-50 text-red-600 text-[10px] px-1.5 py-0.5 rounded font-bold">
                    -{productDetail.discount}%
                  </span>
                )}
              </div>
              {productDetail?.originalPrice > productDetail?.price && (
                <p className="text-xs text-gray-400 dark:text-slate-500 line-through">
                  {formatPrice(productDetail.originalPrice)}₫
                </p>
              )}
              {/* Mobile Gift Section */}
              {productDetail?.gifts && productDetail.gifts.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <div className="flex -space-x-2">
                    <div className="w-7 h-7 rounded-full border-2 border-white dark:border-slate-900 bg-white dark:bg-slate-800 shadow-sm overflow-hidden z-20">
                      <img
                        src={giftDeodorant}
                        alt="Quà tặng 1"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="w-7 h-7 rounded-full border-2 border-white dark:border-slate-900 bg-white dark:bg-slate-800 shadow-sm overflow-hidden z-10">
                      <img
                        src={giftSocks}
                        alt="Quà tặng 2"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <span className="text-[9px] font-bold text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded border border-orange-100 uppercase tracking-tighter">
                    + Quà tặng
                  </span>
                </div>
              )}
              <p className="text-[12px] font-semibold text-gray-800 dark:text-gray-200 line-clamp-2 leading-snug">
                {productDetail?.name}
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-1">
              <span className="text-[10px] bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded border border-green-100 dark:border-green-900/30">
                ✓ Freeship
              </span>
              <span className="text-[10px] bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded border border-blue-100 dark:border-blue-900/30">
                Chính hãng
              </span>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-2 right-2 p-1.5 bg-white/80 dark:bg-slate-800/80 backdrop-blur rounded-full text-gray-500 dark:text-slate-400 shadow-sm"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Desktop Close Button */}
        <button
          onClick={onClose}
          className="hidden sm:block absolute top-4 right-4 z-10 bg-white dark:bg-slate-800 rounded-full p-2 shadow-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors text-gray-500 dark:text-slate-400"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {error && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 m-4">
              <p className="text-sm text-yellow-700">⚠️ {error}</p>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4 sm:gap-5 p-4 sm:p-5">
            <div className="space-y-4 hidden sm:block">
              <div
                className="w-full aspect-[4/3.5] bg-gray-50 dark:bg-slate-800 rounded-xl overflow-hidden border-2 border-gray-200 dark:border-slate-700 relative cursor-zoom-in group/main"
                onClick={() => setIsLightboxOpen(true)}
              >
                <img
                  src={images[selectedImage] || defaultImage}
                  alt={productDetail?.name}
                  className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover/main:scale-105"
                  onError={(e) => {
                    e.target.src = defaultImage;
                  }}
                />
              </div>

              {images.length > 1 && (
                <div className="grid grid-cols-5 gap-1.5">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === index
                          ? "border-blue-600 shadow-md"
                          : "border-gray-200 dark:border-slate-700 hover:border-gray-400 dark:hover:border-slate-500"
                      }`}
                    >
                      <img
                        src={img || defaultImage}
                        alt={`${productDetail?.name} ${index + 1}`}
                        className="w-full h-full object-contain p-0.5"
                        onError={(e) => {
                          e.target.src = defaultImage;
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="hidden sm:block space-y-2.5">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500 uppercase tracking-wide">
                    {productDetail?.brand ||
                      productDetail?.category ||
                      "TAHA SHOES"}
                  </p>
                  {productDetail?.stock > 0 ? (
                    <span className="text-sm text-green-600 font-semibold bg-green-50 px-2 py-1 rounded">
                      ✓ Còn hàng
                    </span>
                  ) : (
                    <span className="text-sm text-red-600 font-semibold bg-red-50 px-2 py-1 rounded">
                      Hết hàng
                    </span>
                  )}
                </div>

                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1 leading-snug">
                    {productDetail?.name}
                  </h2>
                  <div className="flex items-center gap-3">
                    <StarRating
                      rating={productDetail?.rating || 5}
                      size="w-4 h-4"
                    />
                    <span className="text-xs text-gray-500 dark:text-slate-400">
                      ({productDetail?.reviewCount || 0} đánh giá)
                    </span>
                    {productDetail?.soldCount > 0 && (
                      <div className="flex items-center gap-1 ml-2 pl-2 border-l border-gray-300 dark:border-slate-700">
                        <span className="text-xs text-gray-500 dark:text-slate-400 font-medium">
                          Đã bán:
                        </span>
                        <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                          {productDetail.soldCount}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-red-600 dark:text-red-500">
                    {formatPrice(productDetail?.price)}₫
                  </span>
                  {productDetail?.originalPrice > productDetail?.price && (
                    <span className="text-sm text-gray-400 dark:text-slate-500 line-through">
                      {formatPrice(productDetail.originalPrice)}₫
                    </span>
                  )}
                </div>

                {/* Desktop Gift Section */}
                {productDetail?.gifts && productDetail.gifts.length > 0 && (
                  <div className="p-2.5 bg-gradient-to-r from-orange-50/50 to-amber-50/50 dark:from-orange-950/40 dark:to-amber-950/40 border border-orange-100 dark:border-orange-900/30 rounded-lg shadow-sm border-l-4 border-l-orange-500">
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className="text-sm">🎁</span>
                      <h4 className="font-bold text-orange-700 dark:text-orange-400 text-[10px] uppercase tracking-wider">
                        Quà tặng đặc biệt
                      </h4>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-white dark:bg-slate-900/40 p-1.5 rounded-lg border border-orange-50 dark:border-orange-900/10 flex items-center gap-2">
                        <div className="w-9 h-9 overflow-hidden rounded bg-gray-50 dark:bg-slate-800 flex-shrink-0">
                          <img
                            src={giftDeodorant}
                            alt="Chai khử mùi giày"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="text-[10px] font-bold text-gray-700 dark:text-gray-200 leading-tight">
                          Chai khử mùi
                        </span>
                      </div>
                      <div className="text-orange-400 font-bold text-xs">+</div>
                      <div className="flex-1 bg-white dark:bg-slate-900/40 p-1.5 rounded-lg border border-orange-50 dark:border-orange-900/10 flex items-center gap-2">
                        <div className="w-9 h-9 overflow-hidden rounded bg-gray-50 dark:bg-slate-800 flex-shrink-0">
                          <img
                            src={giftSocks}
                            alt="Đôi vớ"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="text-[10px] font-bold text-gray-700 dark:text-gray-200 leading-tight">
                          Đôi vớ
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {productDetail?.colors && productDetail.colors.length > 0 && (
                <div className="space-y-2">
                  <label className="text-[13px] font-bold text-gray-900 dark:text-white uppercase tracking-tight">
                    Màu sắc:{" "}
                    <span className="text-blue-600 dark:text-blue-400 font-medium ml-1">
                      {selectedColor || "Chưa chọn"}
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {productDetail.colors.map((colorObj, idx) => {
                      const colorName =
                        typeof colorObj === "string" ? colorObj : colorObj.name;
                      const colorImage =
                        typeof colorObj === "string" ? null : colorObj.image;
                      const isActive = selectedColor === colorName;
                      return (
                        <button
                          key={idx}
                          onClick={() => {
                            setSelectedColor(colorName);
                            if (colorImage) {
                              const imgIndex = images.indexOf(colorImage);
                              if (imgIndex !== -1) {
                                setSelectedImage(imgIndex);
                              }
                            }
                          }}
                          className={`group flex items-center gap-1.5 p-1 rounded-lg border-2 transition-all ${
                            isActive
                              ? "border-blue-600 bg-blue-50 dark:bg-blue-600 text-blue-600 dark:text-white shadow-sm"
                              : "border-gray-100 dark:border-slate-800 hover:border-gray-300 dark:hover:border-slate-600 bg-white dark:bg-slate-900 text-gray-600 dark:text-slate-400"
                          }`}
                        >
                          {colorImage && (
                            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded overflow-hidden bg-gray-50 flex-shrink-0 border border-gray-100">
                              <img
                                src={colorImage}
                                alt={colorName}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              />
                            </div>
                          )}
                          <span className="px-1.5 text-[12px] font-semibold whitespace-nowrap">
                            {colorName}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {productDetail?.sizes && productDetail.sizes.length > 0 && (
                <div className="space-y-1.5 sm:space-y-2 pt-1">
                  <label className="text-[12px] sm:text-[13px] font-bold text-gray-900 dark:text-white uppercase tracking-tight">
                    Kích thước:{" "}
                    <span className="text-blue-600 dark:text-blue-400 font-medium ml-1">
                      {selectedSize || "Chưa chọn"}
                    </span>
                  </label>
                  <div className="grid grid-cols-5 sm:grid-cols-7 gap-1 sm:gap-1.5">
                    {productDetail.sizes.map((size) => {
                      const isActive = selectedSize === size;
                      return (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(size)}
                          className={`py-1 sm:py-1.5 rounded-lg border text-[11px] sm:text-[12px] font-bold transition-all ${
                            isActive
                              ? "border-blue-600 bg-blue-50 dark:bg-blue-600 text-blue-600 dark:text-white shadow-sm"
                              : "border-gray-200 dark:border-slate-800 text-gray-600 dark:text-slate-400 hover:border-gray-300 dark:hover:border-slate-600"
                          }`}
                        >
                          {size}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-2 pb-2 sm:pb-0">
                <span className="text-[13px] font-bold text-gray-900 dark:text-white uppercase">
                  Số lượng:
                </span>
                <div className="flex items-center border border-gray-300 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-800">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-500 dark:text-slate-400 disabled:opacity-30"
                    disabled={quantity <= 1}
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M20 12H4"
                      />
                    </svg>
                  </button>
                  <span className="w-10 text-center font-bold text-[14px] text-gray-900 dark:text-white">
                    {quantity}
                  </span>
                  <button
                    onClick={() =>
                      setQuantity(
                        Math.min(productDetail?.stock || 100, quantity + 1),
                      )
                    }
                    className="px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-500 dark:text-slate-400"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Desktop Action Buttons - below quantity */}
              <div className="hidden sm:flex gap-2 pt-16 ">
                <button
                  onClick={handleAddToCart}
                  disabled={productDetail?.stock <= 0}
                  className={`flex-1 py-4 px-5 rounded-lg font-bold text-[15px] active:scale-[0.97] transition-all flex items-center justify-center gap-1.5 shadow-sm ${
                    productDetail?.stock <= 0
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed shadow-none"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  <svg
                    className={`w-3.5 h-3.5 ${productDetail?.stock <= 0 ? "hidden" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  <span>
                    {productDetail?.stock <= 0 ? "Hết hàng" : "Thêm giỏ hàng"}
                  </span>
                </button>
                {!onAddToCart && (
                  <button
                    onClick={handleBuyNow}
                    disabled={productDetail?.stock <= 0}
                    className={`flex-1 py-2 px-3 rounded-lg font-bold text-[15px] active:scale-[0.97] transition-all shadow-sm ${
                      productDetail?.stock <= 0
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                        : "bg-red-600 text-white hover:bg-red-700"
                    }`}
                  >
                    {productDetail?.stock <= 0 ? "Liên hệ sau" : "Mua Ngay"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Mobile Bottom Buttons ── */}
        <div className="sm:hidden p-3 bg-white dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800 z-30">
          <div className="flex gap-2">
            <button
              onClick={handleAddToCart}
              disabled={productDetail?.stock <= 0}
              className={`flex-1 py-2.5 sm:py-3 px-4 rounded-xl font-bold text-[13px] active:scale-[0.98] transition-all flex items-center justify-center shadow-lg ${
                productDetail?.stock <= 0
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed shadow-none"
                  : "bg-blue-600 text-white hover:bg-blue-700 shadow-blue-200"
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <svg
                  className={`w-5 h-5 ${productDetail?.stock <= 0 ? "hidden" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <span>
                  {productDetail?.stock <= 0 ? "Hết hàng" : "Thêm giỏ hàng"}
                </span>
              </div>
            </button>
            {!onAddToCart && (
              <button
                onClick={handleBuyNow}
                disabled={productDetail?.stock <= 0}
                className={`flex-1 py-2.5 sm:py-3 px-4 rounded-xl font-bold text-[13px] active:scale-[0.98] transition-all shadow-lg ${
                  productDetail?.stock <= 0
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                    : "bg-red-600 text-white hover:bg-red-700 shadow-red-200"
                }`}
              >
                {productDetail?.stock <= 0 ? "Liên hệ sau" : "Mua Ngay"}
              </button>
            )}
          </div>
        </div>
      </div>

      {isLightboxOpen && (
        <ImageLightbox
          images={images}
          currentIndex={selectedImage}
          onIndexChange={setSelectedImage}
          onClose={() => setIsLightboxOpen(false)}
        />
      )}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { 
          from { transform: translateY(100%); opacity: 0; } 
          to { transform: translateY(0); opacity: 1; } 
        }
        @keyframes zoomIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
        .animate-slideUp { animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-zoomIn { animation: zoomIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #E5E7EB; border-radius: 20px; }
      `}</style>
    </div>,
    document.body,
  );
};

export default ProductQuickView;
